/**
 * The relaii client API v1 wire format, as specified in protocol/client-api.md.
 *
 * Every shape here describes JSON that crosses the network. Nothing in this
 * file is a view model: components may derive whatever they like from these,
 * but the types themselves stay a faithful transcription of the contract so
 * that a server change shows up as a compile error rather than as a blank
 * screen.
 */

// ── Conventions ─────────────────────────────────────────────────────────────

/** UUIDv7, rendered as a string. */
export type UUID = string

/** RFC 3339 with microseconds, always UTC: `2026-08-30T13:04:05.123456Z`. */
export type Timestamp = string

/** Global sync position. Allocated in commit order, so it is gap-free. */
export type Cursor = number

/** Opaque keyset pagination token. Passed straight back as `?cursor=`. */
export type PageCursor = string

/** The envelope every unbounded list endpoint returns. */
export interface Paginated<T> {
  data: T[]
  next_cursor: PageCursor | null
  has_more: boolean
}

// ── Errors ──────────────────────────────────────────────────────────────────

/**
 * Every `code` the server documents. Codes are stable within v1; a code this
 * union does not know about can only arrive from a newer server, which is why
 * consumers should always keep a default branch.
 */
export type ApiErrorCode =
  // 400
  | 'invalid_request'
  | 'invalid_cursor'
  // 401
  | 'unauthenticated'
  | 'invalid_credentials'
  | 'token_expired'
  | 'invalid_refresh_token'
  | 'invalid_mfa_code'
  | 'invalid_mfa_token'
  // 403
  | 'forbidden'
  | 'account_disabled'
  | 'setup_required'
  | 'federation_disabled'
  | 'quota_exceeded'
  | 'mfa_enrollment_required'
  | 'mfa_required_by_server'
  // 404
  | 'not_found'
  // 409
  | 'conflict'
  | 'already_exists'
  | 'owner_exists'
  // 413
  | 'payload_too_large'
  // 422
  | 'validation_failed'
  // 429
  | 'rate_limited'
  // 503
  | 'storage_unavailable'
  | 'not_ready'

/** `details` is free-form and present only for some codes. */
export type ApiErrorDetails = Record<string, unknown>

/** `details` for `validation_failed`: one field maps to its messages. */
export type FieldErrors = Record<string, string[]>

/** `details` for `rate_limited`. */
export interface RateLimitDetails {
  retry_after_seconds: number
}

/** `details` for `quota_exceeded`. */
export interface QuotaDetails {
  requested_bytes: number
  used_bytes: number
  limit_bytes: number
  available_bytes: number
}

/** The body of every non-2xx response. */
export interface ApiErrorBody {
  error: {
    code: ApiErrorCode
    message: string
    details?: ApiErrorDetails
  }
}

/**
 * The structural shape of the error `@/api/client` throws.
 *
 * The class itself lives in `@/api/client` because it needs runtime behaviour;
 * this interface exists so a module can talk about a caught error without
 * importing the client. The two are kept structurally identical on purpose.
 */
export interface ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly details: ApiErrorDetails
  /** Parsed `details` for a 422, empty for every other failure. */
  readonly fieldErrors: FieldErrors
  /** `details.retry_after_seconds` for a 429, otherwise null. */
  readonly retryAfterSeconds: number | null
}

// ── Users ───────────────────────────────────────────────────────────────────

export type UserStatus = 'active' | 'disabled' | 'invited'

export interface User {
  id: UUID
  username: string
  display_name: string
  /** `username@server_id` — globally unique across federation. */
  qualified_id: string
  server_id: string
  is_local: boolean
  is_bot: boolean
  /** Always false for remote users: another server's roles carry no authority. */
  is_admin: boolean
  is_owner: boolean
  status: UserStatus
  avatar_url: string | null
  /** Local profile fields are absent for federated users on older peers. */
  bio?: string | null
  linkedin_url?: string | null
  phone?: string | null
  /**
   * IANA zone, for showing what time it is where somebody is. Present for
   * local accounts that have set one; never for federated users, whose home
   * server does not send it.
   */
  timezone?: string | null
  last_seen_at: Timestamp | null
  /** Present on `/auth/me` and on admin user payloads. True once TOTP is confirmed. */
  mfa_enabled?: boolean
}

/**
 * `GET /auth/me`. The extra fields are returned for the authenticated user
 * only; no endpoint ever discloses another user's email to a non-admin.
 */
export type NotificationPreference = 'all' | 'mentions' | 'dm_and_mentions' | 'nothing'
export type RoomNotificationPreference = 'default' | 'all' | 'mentions' | 'muted'

export interface CurrentUser extends User {
  email: string | null
  /** Always present for the signed-in account, even when it is null. */
  timezone: string | null
  onboarding_completed_at: Timestamp | null
  /** Present on `/auth/me`. Clients that have not been rebuilt ignore it. */
  notification_preference?: NotificationPreference
}

/** A row of `GET /auth/sessions`. */
export interface UserSession {
  id: UUID
  user_agent: string | null
  ip_address: string | null
  last_used_at: Timestamp | null
  expires_at: Timestamp
  created_at: Timestamp
  /** True for the session making the request, where the server marks it. */
  current?: boolean
}

// ── Rooms ───────────────────────────────────────────────────────────────────

export type RoomKind = 'channel' | 'direct' | 'group_direct'
export type RoomVisibility = 'public' | 'private'
export type MemberRole = 'owner' | 'moderator' | 'member' | 'guest'

export interface RoomMembership {
  /** The caller's membership row; needed to star or mute this room. */
  id?: UUID
  role: MemberRole
  joined_at: Timestamp
  muted: boolean
  starred?: boolean
  /** Room override. Absent on older servers; treat as `default`. */
  notification?: RoomNotificationPreference
}

/** The zero reading, for a room with no unread state of its own. */
export const NO_UNREAD: RoomUnread = { count: 0, mentions: 0, last_read_sequence: 0 }

/** Reads a room's unread state, falling back to zero when it has none. */
export function unreadOf(room: { unread: RoomUnread | null }): RoomUnread {
  return room.unread ?? NO_UNREAD
}

/** True when the viewer has silenced the room, by either switch a server may set. */
export function isMutedRoom(room: { my_membership: RoomMembership | null }): boolean {
  const membership = room.my_membership
  if (membership === null || membership === undefined) return false
  return membership.muted || membership.notification === 'muted'
}

/**
 * Whether a room's unread state belongs in a badge, the Activity count or the
 * window title. The viewer has to be in the room — `/sync` also carries the
 * public channels they can see but never joined — and must not have muted it.
 * Every place that adds unread numbers up goes through this, so the sidebar,
 * the Overview and the title can never disagree about which rooms count.
 */
export function countsUnread(room: { my_membership: RoomMembership | null }): boolean {
  return room.my_membership !== null && !isMutedRoom(room)
}

/** The Activity badge: one number per room that counts, never fewer than its mentions. */
export function activityCount(rooms: readonly Room[]): number {
  return rooms.reduce((total, room) => {
    if (!countsUnread(room)) return total
    const unread = unreadOf(room)
    return total + Math.max(unread.count, unread.mentions)
  }, 0)
}

export interface RoomUnread {
  count: number
  mentions: number
  last_read_sequence: number
}

export interface Room {
  id: UUID
  kind: RoomKind
  /** Null for `direct` and `group_direct`; clients render participants instead. */
  name: string | null
  slug: string | null
  topic: string | null
  visibility: RoomVisibility
  /** The room's homeserver. It assigns every `room_sequence` in the room. */
  owner_server_id: string
  is_local: boolean
  /** True when the room is shared across servers in either direction. */
  is_shared: boolean
  /** Present only for rooms this server owns. */
  shared_with?: string[]
  last_sequence: number
  content_revision?: number
  member_count: number
  archived_at: Timestamp | null
  created_at: Timestamp
  /**
   * Null for a room the caller can see but has not joined — a public channel
   * they are not a member of. Anything gated on a room role must treat that as
   * "no role", not assume a membership is present.
   */
  my_membership: RoomMembership | null
  /**
   * Absent for a room the caller can see but is not a member of — a public
   * channel they have not joined. Read it through `unreadOf` rather than
   * dereferencing it.
   */
  unread: RoomUnread | null
}

export interface Member {
  id: UUID
  role: MemberRole
  joined_at: Timestamp
  muted: boolean
  /** Personal fields are present only when the member represents the caller. */
  starred?: boolean
  notification?: RoomNotificationPreference
  user: User
}

// ── Messages and attachments ────────────────────────────────────────────────

export type AttachmentStatus =
  | 'pending'
  | 'uploaded'
  | 'scanning'
  | 'ready'
  | 'blocked'
  | 'deleted'

export interface Attachment {
  id: UUID
  room_id: UUID
  /** Null until the message referencing this attachment is posted. */
  message_id: UUID | null
  filename: string
  mime_type: string
  byte_size: number
  /** `sha256:…`, once the server has verified the bytes. */
  checksum: string | null
  /** A generated WebP rendition is available for inline image display. */
  preview_ready?: boolean
  status: AttachmentStatus
  created_at: Timestamp
}

export interface Message {
  id: UUID
  room_id: UUID
  room_sequence: number
  /**
   * The root of a one-level thread. Null (or absent on older servers and
   * cached rows) for a top-level message. A reply always names its root —
   * never another reply.
   */
  parent_id?: UUID | null
  /** Live direct replies. Absent on older servers; treat as 0. */
  reply_count?: number
  /**
   * Plain text, never HTML. Clients render Markdown into elements themselves.
   * A deleted message is a tombstone: `""` text, no attachments, `deleted_at`
   * set, and the sequence kept so no gap appears.
   */
  text: string
  /** Structured webhook content. Empty or absent for ordinary/basic messages. */
  blocks?: WebhookBlock[]
  sender: User
  /** A display name supplied by an incoming webhook for this one message. */
  sender_override: string | null
  /** An HTTP(S) avatar supplied by an incoming webhook for this one message. */
  sender_avatar_url?: string | null
  client_message_id: string | null
  mentions: UUID[]
  mentions_everyone: boolean
  attachments: Attachment[]
  /** Additive for compatibility with servers before reactions shipped. */
  reactions?: MessageReaction[]
  edited_at: Timestamp | null
  deleted_at: Timestamp | null
  created_at: Timestamp
}

export interface MessageReaction {
  emoji: string
  count: number
  me: boolean
  /** Additive for compatibility with servers that only return reaction counts. */
  actors?: MessageReactionActor[]
}

export interface MessageReactionActor {
  id: UUID
  name: string
  avatar_url?: string | null
}

export interface WebhookTitleBlock {
  type: 'title'
  text: string
}

export interface WebhookTextBlock {
  type: 'text'
  text: string
}

export interface WebhookImageBlock {
  type: 'image'
  url: string
  alt: string
}

export interface WebhookTableBlock {
  type: 'table'
  columns: string[]
  rows: string[][]
}

export interface WebhookActionButton {
  text: string
  url: string
}

export interface WebhookActionsBlock {
  type: 'actions'
  buttons: WebhookActionButton[]
}

export type WebhookBlock =
  | WebhookTitleBlock
  | WebhookTextBlock
  | WebhookImageBlock
  | WebhookTableBlock
  | WebhookActionsBlock

/** `GET /rooms/:id/messages`. Paginates on `room_sequence`, not on a cursor. */
export interface MessagePage {
  messages: Message[]
  has_more: boolean
}

// ── Events ──────────────────────────────────────────────────────────────────

export type DurableEventType =
  | 'message.created'
  | 'message.updated'
  | 'message.deleted'
  | 'message.reaction.added'
  | 'message.reaction.removed'
  | 'room.created'
  | 'room.updated'
  | 'room.deleted'
  | 'member.joined'
  | 'member.left'
  | 'member.updated'
  | 'attachment.created'
  | 'attachment.ready'
  | 'attachment.deleted'

export type EphemeralEventType =
  | 'membership.updated'
  | 'typing.started'
  | 'typing.stopped'
  | 'presence.changed'
  | 'read.updated'
  | 'call.invited'
  | 'call.updated'

export type RelaiiEventType = DurableEventType | EphemeralEventType

/**
 * A persisted change, identical over `/sync` and over the WebSocket so that a
 * client has one code path for applying it.
 */
export interface DurableEventEnvelope<T extends DurableEventType, P> {
  type: T
  event_id: UUID
  cursor: Cursor
  room_id: UUID
  /** Null for events that do not consume a position in the room's sequence. */
  room_sequence: number | null
  created_at: Timestamp
  payload: P
}

/**
 * A signal that exists only on the socket. Never persisted, never replayed,
 * and carries no cursor — applying one must not advance the stored position.
 */
export interface EphemeralEventEnvelope<T extends EphemeralEventType, P> {
  type: T
  room_id: UUID | null
  payload: P
}

export type MessageCreatedEvent = DurableEventEnvelope<'message.created', { message: Message }>
export type MessageUpdatedEvent = DurableEventEnvelope<'message.updated', { message: Message }>
export type MessageDeletedEvent = DurableEventEnvelope<
  'message.deleted',
  { message_id: UUID; deleted_at: Timestamp }
>
export interface ReactionActor {
  user_id?: UUID | null
  remote_user_id?: string | null
  qualified_id?: string | null
  server_id?: string | null
}
export type MessageReactionEvent = DurableEventEnvelope<
  'message.reaction.added' | 'message.reaction.removed',
  { message_id: UUID; emoji: string; count: number; actor?: ReactionActor }
>
export type RoomCreatedEvent = DurableEventEnvelope<'room.created', { room: Room }>
export type RoomUpdatedEvent = DurableEventEnvelope<'room.updated', { room: Room }>
export type RoomDeletedEvent = DurableEventEnvelope<'room.deleted', { room_id: UUID }>
export type MemberJoinedEvent = DurableEventEnvelope<'member.joined', { member: Member }>
export type MemberLeftEvent = DurableEventEnvelope<
  'member.left',
  { member_id: UUID; user_id: UUID }
>
export type MemberUpdatedEvent = DurableEventEnvelope<'member.updated', { member: Member }>
export type AttachmentCreatedEvent = DurableEventEnvelope<
  'attachment.created',
  { attachment: Attachment }
>
export type AttachmentReadyEvent = DurableEventEnvelope<
  'attachment.ready',
  { attachment: Attachment }
>
export type AttachmentDeletedEvent = DurableEventEnvelope<
  'attachment.deleted',
  { attachment_id: UUID }
>

export type PresenceStatus = 'online' | 'away' | 'offline'

export type TypingStartedEvent = EphemeralEventEnvelope<'typing.started', { user_id: UUID }>
export type TypingStoppedEvent = EphemeralEventEnvelope<'typing.stopped', { user_id: UUID }>
export type PresenceChangedEvent = EphemeralEventEnvelope<
  'presence.changed',
  { user_id: UUID; status: PresenceStatus; last_seen_at: Timestamp | null }
>
export type ReadUpdatedEvent = EphemeralEventEnvelope<
  'read.updated',
  { unread: RoomUnread }
>
export type MembershipUpdatedEvent = EphemeralEventEnvelope<
  'membership.updated',
  { membership: RoomMembership }
>

export type CallMedia = 'audio' | 'video'
export type CallStatus = 'ringing' | 'active' | 'ended'
export type CallParticipantStatus = 'invited' | 'joined' | 'declined' | 'left'

export interface CallParticipant {
  user: User | null
  status: CallParticipantStatus
  joined_at: Timestamp | null
  left_at: Timestamp | null
  /** Holding the call's one shared surface. Derived from the call. */
  sharing?: boolean
  /** What this participant's client can render — see calls-v2. */
  features?: string[]
}

/** What is being shared: one window, a whole screen, or a browser tab. */
export type CallShareSource = 'window' | 'screen' | 'tab'

/** Voice or video conversation in one room — see protocol/calls-v1.md. */
export interface Call {
  id: UUID
  room_id: UUID
  media: CallMedia
  status: CallStatus
  initiator: User | null
  participants: CallParticipant[]
  started_at: Timestamp
  ended_at: Timestamp | null
  /**
   * Who holds the shared surface, if anyone. On the call and not only on the
   * signal, so someone arriving late can be told in the pre-join.
   */
  sharing_user_id?: UUID | null
  sharing_source?: CallShareSource | null
  /** The media stream the screen is arriving on, as the sharer named it. */
  sharing_stream_id?: string | null
}

export type CallInvitedEvent = EphemeralEventEnvelope<'call.invited', { call: Call }>
export type CallUpdatedEvent = EphemeralEventEnvelope<'call.updated', { call: Call }>

export type DurableEvent =
  | MessageCreatedEvent
  | MessageUpdatedEvent
  | MessageDeletedEvent
  | MessageReactionEvent
  | RoomCreatedEvent
  | RoomUpdatedEvent
  | RoomDeletedEvent
  | MemberJoinedEvent
  | MemberLeftEvent
  | MemberUpdatedEvent
  | AttachmentCreatedEvent
  | AttachmentReadyEvent
  | AttachmentDeletedEvent

export type EphemeralEvent =
  | MembershipUpdatedEvent
  | TypingStartedEvent
  | TypingStoppedEvent
  | PresenceChangedEvent
  | ReadUpdatedEvent
  | CallInvitedEvent
  | CallUpdatedEvent

/** Discriminated on `type`, so a reducer over it gets exhaustiveness checking. */
export type RelaiiEvent = DurableEvent | EphemeralEvent

export const DURABLE_EVENT_TYPES = [
  'message.created',
  'message.updated',
  'message.deleted',
  'message.reaction.added',
  'message.reaction.removed',
  'room.created',
  'room.updated',
  'room.deleted',
  'member.joined',
  'member.left',
  'member.updated',
  'attachment.created',
  'attachment.ready',
  'attachment.deleted',
] as const satisfies readonly DurableEventType[]

export const EPHEMERAL_EVENT_TYPES = [
  'membership.updated',
  'typing.started',
  'typing.stopped',
  'presence.changed',
  'read.updated',
  'call.invited',
  'call.updated',
] as const satisfies readonly EphemeralEventType[]

const DURABLE_TYPE_SET: ReadonlySet<string> = new Set<string>(DURABLE_EVENT_TYPES)

export function isDurableEvent(event: RelaiiEvent): event is DurableEvent {
  return DURABLE_TYPE_SET.has(event.type)
}

export function isEphemeralEvent(event: RelaiiEvent): event is EphemeralEvent {
  return !DURABLE_TYPE_SET.has(event.type)
}

/**
 * Exhaustiveness guard. Reaching it means a `switch` over a discriminated
 * union grew a case the code does not handle, which the compiler now rejects
 * at the call site rather than letting the event fall silently on the floor.
 */
export function assertNever(value: never): never {
  throw new Error(`Unhandled variant: ${JSON.stringify(value)}`)
}

// ── Sync and realtime ───────────────────────────────────────────────────────

export interface SyncResponse {
  events: DurableEvent[]
  /** The highest cursor the client may store after applying `events`. */
  cursor: Cursor
  has_more: boolean
  rooms: Room[]
  /** Private read watermarks, returned even on an incremental sync. */
  read_states?: Record<UUID, RoomUnread>
  /** True when the client's cursor is too old: discard the cache and bootstrap. */
  reset: boolean
}

export interface SyncParams {
  since?: Cursor
  limit?: number
}

/** Payload sent when joining the `user:<user_id>` topic. */
export interface UserTopicJoin {
  since?: Cursor
}

/** Reply to a `user:<user_id>` join. A non-zero `missed` means call `/sync`. */
export interface UserTopicJoinReply {
  cursor: Cursor
  missed: number
}

/** Client-to-server on a room topic. Best effort. */
export interface TypingSignal {
  typing: boolean
}

/** Client-to-server on a room topic. Best effort. */
export interface ReadSignal {
  sequence: number
}

// ── Server identity, setup and policy ───────────────────────────────────────

export type RegistrationMode = 'invite_only' | 'email_domains' | 'open'
export type FederationMode = 'disabled' | 'approved_peers'
export type StorageBackend = 'local' | 's3'

/** The actions an admin can pin to a minimum role. */
export type PermissionAction =
  | 'create_channels'
  | 'create_group_chats'
  | 'invite_users'
  | 'create_webhooks'
  | 'manage_widgets'
  | 'upload_files'

export type PermissionRole = 'owner' | 'admin' | 'moderator' | 'member'

export type ServerPermissions = Record<PermissionAction, PermissionRole>

/** `GET /setup/status` — the only endpoint reachable on a fresh install. */
export interface SetupStatus {
  setup_required: boolean
  server_id: string
  product_version: string
}

/** `GET /server` — identity and the policy a client needs to render itself. */
export interface ServerInfo {
  server_id: string
  base_url: string
  display_name: string | null
  description: string | null
  logo_url: string | null
  product_version: string
  client_api_versions: number[]
  federation_versions: number[]
  websocket_url: string
  registration_mode: RegistrationMode
  federation_enabled: boolean
  message_editing_enabled: boolean
  message_deleting_enabled: boolean
  /** Null means edits never expire. */
  message_edit_window_seconds: number | null
  /**
   * Characters a message body may hold. Absent on servers older than this
   * field; the composer falls back to the value the server has always used.
   */
  message_max_length?: number
  max_upload_bytes: number
  setup_required: boolean
  require_mfa?: boolean
  capabilities?: {
    federation: boolean
    file_uploads: boolean
    search: boolean
    webhooks: boolean
    websocket: boolean
    email: boolean
    password_reset: boolean
    push: boolean
    mfa?: boolean
    saved_messages?: boolean
    room_content?: boolean
    commands?: boolean
    giphy?: boolean
    integration_api?: boolean
    url_widgets?: boolean
    overview?: boolean
    activity_filters?: boolean
    calls?: boolean
    call_screen_sharing?: boolean
  }
}

/** `GET /admin/settings` — everything an admin may change from the browser. */
export interface ServerSettings {
  server_id: string
  display_name: string | null
  description: string | null
  logo_url: string | null
  setup_completed_at: Timestamp | null
  onboarding_completed_at: Timestamp | null
  onboarding_version: number
  registration_mode: RegistrationMode
  allowed_email_domains: string[]
  federation_enabled: boolean
  federation_mode: FederationMode
  permissions: ServerPermissions
  message_editing_enabled: boolean
  message_deleting_enabled: boolean
  message_edit_window_seconds: number | null
  retention_days: number | null
  /** Off until an administrator enables voice and video. */
  calls_enabled?: boolean
  /** Off until an owner enables the optional relaii Push Service. */
  push_enabled: boolean
  push_registered_at: Timestamp | null
  email_provider?: 'smtp' | 'brevo' | 'sendgrid' | 'mailgun' | 'postmark' | 'none'
  email_from?: string | null
  email_api_key_configured?: boolean
  require_mfa?: boolean
  updated_at: Timestamp
}

export type StorageState = 'ok' | 'warning' | 'critical' | 'full' | 'unknown'

/**
 * `GET /admin/storage`. Only `used_bytes`, `state` and `backend` are always
 * present: the rest depends on the backend and on whether the volume could be
 * measured at all.
 */
export interface StorageStatus {
  backend: StorageBackend
  state: StorageState
  used_bytes: number
  limit_bytes?: number
  available_bytes?: number
  usage_ratio?: number
  margin_ratio?: number
  disk_total_bytes?: number
  disk_available_bytes?: number
  max_upload_bytes?: number
  path?: string
  /** True when an operator pinned a fixed ceiling instead of computing one. */
  fixed_limit?: boolean
  error?: string | null
  /**
   * The server renders every `*_bytes` figure above as a `*_human` string in
   * binary units — the same ones `relaii doctor` prints and `docs/storage.md`
   * documents. Operator-facing capacity is shown from these, never from the
   * bytes put through the SI `formatBytes`.
   */
  used_human?: string
  limit_human?: string
  available_human?: string
  disk_total_human?: string
  disk_available_human?: string
  max_upload_human?: string
}

export type OnboardingStep =
  | 'organisation'
  | 'access'
  | 'permissions'
  | 'federation'
  | 'files'
  | 'messages'
  | 'channel'

/**
 * `GET /onboarding`. Carries the operator's environment as well as policy,
 * because two of the steps report choices made outside the browser and can
 * only be shown, never changed.
 */
export interface OnboardingState {
  settings: ServerSettings
  server_id: string
  version: number
  target_version: number
  completed: boolean
  completed_at: Timestamp | null
  setup_completed_at: Timestamp | null
  steps: OnboardingStep[]
  storage: StorageStatus
  smtp_configured: boolean
  registration_modes: RegistrationMode[]
  federation_modes: FederationMode[]
  default_permissions: ServerPermissions
}

/** `GET /admin/status`. */
export interface AdminStatus {
  server_id: string
  product_version: string
  uptime_seconds: number
  user_count: number
  room_count: number
  message_count: number
  storage: StorageStatus
  smtp_configured: boolean
  federation: {
    enabled: boolean
    peer_count: number
    active_peer_count: number
    outbox_pending: number
  }
}

/** `GET /admin/updates` — a deliberate check against install.relaii.app. */
export interface UpdateCheck {
  status: 'up_to_date' | 'update_available' | 'unavailable'
  current_version: string
  latest_version: string | null
  source: string
  checked_at: Timestamp
  message: string | null
}

// ── Invitations ─────────────────────────────────────────────────────────────

export type InvitationRole = 'admin' | 'member' | 'guest'
export type InvitationKind = 'email' | 'code' | 'qr'

export interface Invitation {
  /** Returned only when an invitation is created. */
  invite_url?: string
  invite_code?: string
  email_delivery?: 'queued' | 'not_requested'
  id: UUID
  name?: string | null
  email: string | null
  kind: InvitationKind
  role: InvitationRole
  room_ids: UUID[]
  max_uses?: number | null
  use_count?: number
  requires_approval?: boolean
  expires_at: Timestamp
  accepted_at: Timestamp | null
  revoked_at: Timestamp | null
  invited_by_id: UUID | null
  accepted_user_id: UUID | null
  created_at: Timestamp
}

/** `GET /invitations/:token` — the unauthenticated view shown on the accept page. */
export interface InvitationPreview {
  name?: string | null
  email: string | null
  kind: InvitationKind
  role: InvitationRole
  requires_approval?: boolean
  server_id: string
  server_display_name: string | null
  /** The Space's logo, so the invitation wears the same face as the sign-in screen. */
  server_logo_url: string | null
  invited_by: string | null
  expires_at: Timestamp
}

// ── Webhooks ────────────────────────────────────────────────────────────────

export interface Webhook {
  id: UUID
  room_id: UUID
  name: string
  /** The readable head of the secret, for telling webhooks apart in a list. */
  secret_prefix: string
  bot_user_id: UUID
  enabled: boolean
  rate_limit_per_minute: number
  last_used_at: Timestamp | null
  created_at: Timestamp
}

/**
 * The reply to creating or rotating a webhook. The secret is shown here once
 * and is never retrievable again.
 */
export interface WebhookSecret {
  webhook: Webhook
  secret: string
  url: string
}

/** Channels shown by the server-wide webhook administration page. */
export interface AdminWebhookChannelsResponse {
  channels: Room[]
}

// ── Federation ──────────────────────────────────────────────────────────────

export type PeerStatus = 'pending' | 'verifying' | 'active' | 'revoked' | 'error'
export type PeerDirection = 'outbound' | 'inbound'

export interface PeerPermissions {
  share_rooms: boolean
  invite_users: boolean
}

export interface Peer {
  id: UUID
  server_id: string
  base_url: string | null
  status: PeerStatus
  direction: PeerDirection
  display_name: string | null
  fingerprint: string | null
  permissions: PeerPermissions
  federation_versions: number[]
  verified_at: Timestamp | null
  last_seen_at: Timestamp | null
  created_at: Timestamp
}

export interface FederationPublicKey {
  id: string
  algorithm: string
  /** Base64 encoded. */
  key: string
  created_at: Timestamp
  expires_at: Timestamp | null
}

/** `GET /admin/federation/identity` — what an admin reads out to a partner. */
export interface FederationIdentity {
  server_id: string
  base_url: string
  fingerprint: string
  federation_versions: number[]
  keys: FederationPublicKey[]
}

/** The reply to `POST /admin/federation/peers/bootstrap`. */
export interface PeerBootstrap {
  peer: Peer
  server_id: string
  base_url: string
  /** Handed to the other administrator out of band. Shown once. */
  bootstrap_key: string
  expires_at: Timestamp
}

/** A row of `GET /admin/federation/outbox`. */
export interface OutboxEntry {
  id: UUID
  peer_server_id: string
  room_id: UUID | null
  event_type: string
  status: string
  attempts: number
  last_error: string | null
  next_attempt_at: Timestamp | null
  created_at: Timestamp
}

export interface RoomSharePermissions {
  read: boolean
  post: boolean
  upload: boolean
  invite_users: boolean
  /** Off by default: a partner cannot pass a channel on to a third server. */
  share_further: boolean
}

export type RoomShareStatus = 'invited' | 'active' | 'rejected' | 'revoked'

export interface RoomShare {
  id: UUID
  room_id: UUID
  peer_server_id: string
  permissions: RoomSharePermissions
  status: RoomShareStatus
  responded_at: Timestamp | null
  created_at: Timestamp
}

// ── Audit ───────────────────────────────────────────────────────────────────

export interface AuditEvent {
  id: UUID
  action: string
  actor_user_id: UUID | null
  actor_peer_server_id: string | null
  actor_label: string | null
  target_type: string | null
  target_id: string | null
  metadata: Record<string, unknown>
  created_at: Timestamp
}

// ── Files ───────────────────────────────────────────────────────────────────

/** Step 2 of the upload flow. The URL is the credential; send no relaii auth. */
export interface UploadInstructions {
  method: 'PUT'
  url: string
  headers: Record<string, string>
  expires_at: Timestamp
}

/** The reply to `POST /rooms/:id/uploads`. Quota is charged here. */
export interface UploadSession {
  attachment: Attachment
  upload: UploadInstructions
}

/** The reply to `POST /auth/me/avatar/uploads`. */
export interface ProfileImageUploadSession {
  profile_image_upload_id: UUID
  upload: UploadInstructions
}

/** The reply to `POST /admin/settings/logo/uploads`. */
export interface LogoUploadSession {
  logo_upload_id: UUID
  upload: UploadInstructions
}

/** `GET /attachments/:id/download` with `Accept: application/json`. */
export interface DownloadLink {
  url: string
  expires_at: Timestamp
}

// ── Search ──────────────────────────────────────────────────────────────────

export interface SearchHit {
  message: Message
  room: Room
}

/**
 * `GET /search`. The query supports `from:`, `in:#`, `before:`, `after:` and
 * `has:file` alongside free text; results are restricted in SQL to rooms the
 * caller may read.
 */
export type SearchResults = Paginated<SearchHit>

/** `GET /activity` — unread messages across every joined room. */
export type ActivityEntry = SearchHit
export type ActivityResults = Paginated<ActivityEntry>

export interface SearchParams {
  q: string
  limit?: number
  cursor?: PageCursor
}

// ── Request bodies ──────────────────────────────────────────────────────────

/** `login` accepts a username or an email address. */
export interface LoginRequest {
  login: string
  password: string
}

export interface RefreshRequest {
  /** Bearer clients send the token; cookie clients send nothing. */
  refresh_token?: string
}

export interface RegisterRequest {
  username: string
  email: string
  password: string
  display_name?: string
  timezone?: string
}

export interface SetupOwnerRequest {
  username: string
  email: string
  password: string
  display_name?: string
  timezone?: string
  server_display_name?: string
}

export interface PasswordResetRequest {
  email: string
}

export interface PasswordResetConfirmRequest {
  token: string
  password: string
}

export interface ChangePasswordRequest {
  current_password: string
  password: string
}

export interface UpdateProfileRequest {
  display_name?: string
  timezone?: string | null
  bio?: string | null
  linkedin_url?: string | null
  phone?: string | null
  notification_preference?: NotificationPreference
}

export interface AcceptInvitationRequest {
  username: string
  password: string
  password_confirmation: string
  display_name?: string
  /** Required when the invitation was not addressed to an email address. */
  email?: string
  timezone?: string
}

export interface CreateRoomRequest {
  kind: RoomKind
  /** Required for channels; omitted for direct and group rooms. */
  name?: string
  slug?: string
  topic?: string
  visibility?: RoomVisibility
  /** Local members to seed the room with. */
  member_ids?: UUID[]
  /** Remote members, as `username@server_id`. */
  qualified_ids?: string[]
}

export interface UpdateRoomRequest {
  name?: string
  topic?: string | null
  visibility?: RoomVisibility
  archived_at?: Timestamp | null
}

export interface AddMembersRequest {
  member_ids?: UUID[]
  qualified_ids?: string[]
  role?: MemberRole
}

export interface UpdateMemberRequest {
  role?: MemberRole
  muted?: boolean
  notification_preference?: RoomNotificationPreference
}

/**
 * `client_message_id` makes sending idempotent: re-posting the same one
 * returns 200 with the original message. Always send one.
 */
export interface CreateMessageRequest {
  text: string
  client_message_id?: string
  attachment_ids?: UUID[]
  /** Root message this is a reply to. Omit for a top-level message. */
  parent_id?: UUID
}

export interface UpdateMessageRequest {
  text: string
}

export interface CreateUploadRequest {
  filename: string
  mime_type: string
  byte_size: number
}

export interface FinalizeUploadRequest {
  checksum?: string
}

export interface ReadStateRequest {
  sequence: number
}

export interface TypingRequest {
  typing: boolean
}

export interface CreateWebhookRequest {
  name: string
  rate_limit_per_minute?: number
}

export interface UpdateWebhookRequest {
  name?: string
  enabled?: boolean
  rate_limit_per_minute?: number
}

/** `POST /hooks/:secret`, backwards-compatible plain-text form. */
export interface BasicWebhookIngressRequest {
  format?: 'basic'
  text: string
  name?: string
  /** Legacy alias for `name`. */
  sender?: string
  avatar_url?: string
  external_id?: string
}

export interface BlocksWebhookIngressRequest {
  format: 'blocks'
  blocks: WebhookBlock[]
  /** Optional fallback; the server derives one from the blocks when omitted. */
  text?: string
  name?: string
  sender?: string
  avatar_url?: string
  external_id?: string
}

export type WebhookIngressRequest = BasicWebhookIngressRequest | BlocksWebhookIngressRequest

export interface CreateShareRequest {
  peer_server_id: string
  permissions?: Partial<RoomSharePermissions>
}

export interface UpdateShareRequest {
  permissions?: Partial<RoomSharePermissions>
  status?: RoomShareStatus
}

export interface UpdateSettingsRequest {
  display_name?: string
  description?: string | null
  logo_object_key?: string | null
  registration_mode?: RegistrationMode
  allowed_email_domains?: string[]
  federation_enabled?: boolean
  federation_mode?: FederationMode
  permissions?: Partial<ServerPermissions>
  message_editing_enabled?: boolean
  message_deleting_enabled?: boolean
  message_edit_window_seconds?: number | null
  retention_days?: number | null
  calls_enabled?: boolean
  push_enabled?: boolean
  require_mfa?: boolean
  email_provider?: 'smtp' | 'brevo' | 'sendgrid' | 'mailgun' | 'postmark' | 'none'
  email_from?: string | null
  /** Write-only. The API never returns this value. */
  email_api_key?: string | null
}

export interface OnboardingOrganisationStep {
  display_name?: string
  description?: string | null
  logo_object_key?: string | null
}

export interface OnboardingAccessStep {
  registration_mode?: RegistrationMode
  allowed_email_domains?: string[]
}

export interface OnboardingPermissionsStep {
  permissions?: Partial<ServerPermissions>
}

export interface OnboardingFederationStep {
  federation_enabled?: boolean
  federation_mode?: FederationMode
}

/** Storage is an operator decision; the step is acknowledged, never applied. */
export interface OnboardingFilesStep {
  acknowledged?: boolean
}

export interface OnboardingMessagesStep {
  message_editing_enabled?: boolean
  message_deleting_enabled?: boolean
  message_edit_window_seconds?: number | null
  retention_days?: number | null
}

export interface OnboardingChannelStep {
  name?: string
  topic?: string
  visibility?: RoomVisibility
}

/** `POST /onboarding`. Every step is optional; unknown keys are ignored. */
export interface OnboardingRequest {
  organisation?: OnboardingOrganisationStep
  access?: OnboardingAccessStep
  permissions?: OnboardingPermissionsStep
  federation?: OnboardingFederationStep
  files?: OnboardingFilesStep
  messages?: OnboardingMessagesStep
  channel?: OnboardingChannelStep
}

export interface CreateUserRequest {
  username: string
  email: string
  display_name?: string
  password?: string
  is_admin?: boolean
}

export interface UpdateUserRequest {
  display_name?: string
  email?: string
  is_admin?: boolean
  status?: UserStatus
}

export interface CreateInvitationRequest {
  name?: string
  send_email?: boolean
  kind?: InvitationKind
  email?: string
  role?: InvitationRole
  room_ids?: UUID[]
  max_uses?: number | null
  requires_approval?: boolean
}

export interface TransferOwnershipRequest {
  user_id: UUID
  password: string
}

/** Starts a handshake this server initiates: it issues the bootstrap key. */
export interface PeerBootstrapRequest {
  server_id: string
  display_name?: string
}

/** Completes a handshake the other server started, using its bootstrap key. */
export interface PeerConnectRequest {
  server_id: string
  base_url: string
  bootstrap_key: string
  display_name?: string
}

// ── Response envelopes for the collection endpoints ─────────────────────────

export interface RoomsResponse {
  rooms: Room[]
}

export interface MembersResponse {
  members: Member[]
}

export interface UsersResponse {
  users: User[]
}

/**
 * `GET /rooms/:id/attachments`. A room's files grow without bound, so this is
 * the keyset page of `protocol/client-api.md` section 1 rather than a list: a
 * caller that ignores `next_cursor` is looking at the newest page only.
 */
export type AttachmentsPage = Paginated<Attachment>

export interface WebhooksResponse {
  webhooks: Webhook[]
}

export interface SharesResponse {
  shares: RoomShare[]
}

export interface PeersResponse {
  peers: Peer[]
}

export interface InvitationsResponse {
  invitations: Invitation[]
}

export interface SessionsResponse {
  sessions: UserSession[]
}

export type AuditLogResponse = Paginated<AuditEvent>

export type OutboxResponse = Paginated<OutboxEntry>

// ── Auth responses ──────────────────────────────────────────────────────────

/**
 * Tokens are returned in the body for bearer clients only. The web client
 * authenticates with HttpOnly cookies, so these are absent for it.
 */
export interface AuthTokens {
  access_token?: string
  refresh_token?: string
  expires_at?: Timestamp
}

export interface AuthSession extends AuthTokens {
  user: CurrentUser
}

export interface MfaChallenge {
  mfa_required: true
  mfa_token: string
  expires_in: number
}

export interface MfaStatus {
  enabled: boolean
  pending: boolean
  enabled_at: Timestamp | null
  remaining_recovery_codes: number
}

export interface MfaSetup {
  secret: string
  otpauth_url: string
  issuer: string
}

export interface MfaEnableResponse {
  user: CurrentUser
  recovery_codes: string[]
}

export function isMfaChallenge(value: unknown): value is MfaChallenge {
  if (typeof value !== 'object' || value === null) return false
  const record = value as Record<string, unknown>
  return record['mfa_required'] === true && typeof record['mfa_token'] === 'string'
}

// ── Native devices ──────────────────────────────────────────────────────────

/**
 * A binding stored on the selfhosted server. The APNs (or future FCM) token
 * never appears here — only the opaque id issued by notifications.relaii.app.
 */
export interface DeviceBinding {
  id: UUID
  device_id: string
  push_device_id: string
  platform: 'ios' | 'macos' | 'apple' | 'android'
  enabled: boolean
  last_seen_at: Timestamp | null
  created_at: Timestamp
}

export interface DevicesResponse {
  devices: DeviceBinding[]
}

export interface CreateDeviceRequest {
  device_id: string
  push_device_id: string
  platform: 'ios' | 'macos' | 'apple' | 'android'
}
